import axios from "axios";
import clsx from "clsx";
import React, { lazy, Suspense, useRef, useState } from "react";
// @material-ui/core components
import Box from "@material-ui/core/Box";
import Container from "@material-ui/core/Container";
import Grid from "@material-ui/core/Grid";
import { makeStyles } from "@material-ui/core/styles";
import Helmet from "react-helmet";
// @material-ui/icons components
import AppleIcon from "@material-ui/icons/Apple";
import AttachMoneyIcon from "@material-ui/icons/AttachMoney";
import Warning from "@material-ui/icons/Warning";

// core components
import CardDailyPerformance from "components/Cards/Alternative/CardDailyPerformance.js";
import CardDividends from "components/Cards/Cards/CardDividends";
import CardMarketPerformance from "components/Cards/Cards/CardMarketPerformance";
import CardNews from "components/Cards/Cards/CardNews";
import CardPortfolioBreakdown from "components/Cards/Charts/CardPortfolioBreakdown";
import CardRiskStockClusters from "components/Cards/Charts/CardRiskStockClusters";
import CardQuickInfo from "components/Cards/Widgets/CardQuickInfo";
import AlternativeHeader from "components/Headers/AlternativeHeader.js";

import componentStylesCardDeck from "assets/theme/components/cards/card-deck.js";
import componentStyles from "assets/theme/views/admin/alternative-dashboard.js";

import CardSectorBreakdownHorizontal from "components/Cards/Charts/CardSectorBreakdownHorizontal";
import CardPerformanceByDate from "components/Cards/Tabbed/CardPerformancebyDate";
import { useTranslation } from "react-i18next";
import { useEtoroUser } from "../../../contexts/EtoroUserContext";
import { useService } from "../../../contexts/ServiceContext";
import CardLatestPersonalTrades from "components/Cards/Sortable/CardLatestPersonalTrades";

const useStyles = makeStyles(componentStyles);
const useStylesCardDeck = makeStyles(componentStylesCardDeck);

const CardMapPerformance = lazy(() => import("components/Cards/Alternative/CardMapPerformance"));
const placeholderImg = "";

function AlternativeDashboard() {
  const classes = { ...useStyles(), ...useStylesCardDeck() };
  const [updates, setUpdates] = useState(0);
  const trRef = useRef();
  const { getNews, getMarketInfo, getTrPin, loginTr } = useService();
  const { currentEtoroUserData } = useEtoroUser();
  const [marketInfo, setMarketInfo] = React.useState();
  const [updatedNews, setUpdatedNews] = React.useState();
  const { t, i18n } = useTranslation("translation", { keyPrefix: "Dashboard" });
  let id = "88f770f0-fb89-49b0-b985-b660bf2c5973";

  async function updateData() {
    try {
      if (
        currentEtoroUserData?.marketInfo &&
        currentEtoroUserData?.userInstruments?.instruments &&
        currentEtoroUserData.userInstruments.instruments.length > 0
      ) {
        const responses = await axios.all([
          getMarketInfo(),
          getNews(currentEtoroUserData.userInstruments.instruments),
        ]);
        setMarketInfo(responses[0].data);
        setUpdatedNews(responses[1].data);
      }
    } catch (err) {
      // TODO add error
      return "error";
    }
  }

  React.useEffect(() => {
    updateData();
    return () => {
      setMarketInfo({});
      setUpdatedNews({});
    };
  }, [currentEtoroUserData]);

  React.useEffect(() => {
    setMarketInfo(currentEtoroUserData?.marketInfo);
    setUpdatedNews(currentEtoroUserData?.latestNews);
    return () => {
      setMarketInfo({});
      setUpdatedNews({});
    };
  }, []);

  return (
    <>
      <AlternativeHeader section={t("title")} />
      {/* Page content */}
      <Helmet>
        <title>Dashboard | Bullsheet</title>
      </Helmet>
      <Container
        maxWidth={false}
        component={Box}
        marginTop="-4.5rem"
        classes={{ root: classes.containerRoot }}
      >
        <Box className={clsx(classes.cardDeck, classes.flexColumnFlexXlRow)}>
          <CardPortfolioBreakdown darkMode={true} />
          <CardDividends portfolioDividends={currentEtoroUserData?.portfolioDividends} />
          <CardSectorBreakdownHorizontal />
        </Box>
        <Grid container>
          <Grid item xs={12} md={6} xl={6}>
            <CardDailyPerformance />
          </Grid>
          <Grid item xs={12} md={6} xl={6}>
            <CardRiskStockClusters />
            <CardPerformanceByDate />
          </Grid>
          <Grid item xs={12} xl={7}>
            <Grid container>
              {currentEtoroUserData?.riskContributers ? (
                <Grid item xs={6} sm={3}>
                  <CardQuickInfo
                    style={{ fontWeight: "400" }}
                    subtitle={t("monthlyAvgRiskScore")}
                    title={currentEtoroUserData?.riskContributers?.riskScore?.toString()}
                    icon={Warning}
                    color="bgGradientGlass2"
                  />{" "}
                </Grid>
              ) : (
                <></>
              )}

              {currentEtoroUserData?.userInstruments ? (
                <Grid item xs={6} sm={3}>
                  <CardQuickInfo
                    subtitle={t("monthlyMaxRiskScore")}
                    title={currentEtoroUserData?.currentPortfolio?.tradingStats?.maxMonthlyRiskScore?.toString()}
                    icon={AppleIcon}
                    color="bgGradientGlass2"
                  />
                </Grid>
              ) : (
                <></>
              )}
              <Grid item xs={6} sm={3}>
                <CardQuickInfo
                  subtitle={t("daysSinceRegistration")}
                  title={
                    currentEtoroUserData?.currentPortfolio?.tradingStats?.daysSinceRegistration
                  }
                  icon={AttachMoneyIcon}
                  color="bgGradientGlass2"
                />
              </Grid>
              <Grid item xs={6} sm={3}>
                <CardQuickInfo
                  subtitle={t("activeWeeksThisQuarterPct")}
                  title={currentEtoroUserData?.currentPortfolio?.tradingStats?.activeWeeksPct + "%"}
                  icon={AttachMoneyIcon}
                  color="bgGradientGlass2"
                />
              </Grid>
            </Grid>
            <CardMapPerformance />
          </Grid>
          <Grid item xs={12} xl={5}>
            <CardLatestPersonalTrades />
          </Grid>
        </Grid>
        <Box fontSize="1.1rem" marginTop="0rem" marginBottom="1rem" fontWeight="700">
          {t("marketsPerformance")}
        </Box>
        {marketInfo?.latestMarketPrices && marketInfo.latestMarketPrices.length > 0 ? (
          <Grid container>
            <Grid item xs={6} md={4} xl={2}>
              <CardMarketPerformance
                direction={
                  marketInfo?.latestMarketPrices[1]?.priceChangePercentage < 0 ? "Down" : "Up"
                }
                title="S&P 500"
                subtitle={marketInfo?.latestMarketPrices[1]?.price.toString()}
                change={marketInfo?.latestMarketPrices[1]?.priceChangePercentage}
              />
            </Grid>
            <Grid item xs={6} md={4} xl={2}>
              <CardMarketPerformance
                direction={
                  marketInfo?.latestMarketPrices[3]?.priceChangePercentage < 0 ? "Down" : "Up"
                }
                title="DJ 30"
                subtitle={marketInfo?.latestMarketPrices[3]?.price.toString()}
                change={marketInfo?.latestMarketPrices[3]?.priceChangePercentage}
              />
            </Grid>
            <Grid item xs={6} md={4} xl={2}>
              <CardMarketPerformance
                direction={
                  marketInfo?.latestMarketPrices[2]?.priceChangePercentage < 0 ? "Down" : "Up"
                }
                title="NDQ 100"
                subtitle={marketInfo?.latestMarketPrices[2]?.price.toString()}
                change={marketInfo?.latestMarketPrices[2]?.priceChangePercentage}
              />
            </Grid>
            <Grid item xs={6} md={4} xl={2}>
              <CardMarketPerformance
                direction={
                  marketInfo?.latestMarketPrices[5]?.priceChangePercentage < 0 ? "Down" : "Up"
                }
                title="DAX"
                subtitle={marketInfo?.latestMarketPrices[5]?.price.toString()}
                change={marketInfo?.latestMarketPrices[5]?.priceChangePercentage}
              />
            </Grid>
            <Grid item xs={6} md={4} xl={2}>
              <CardMarketPerformance
                direction={
                  marketInfo?.latestMarketPrices[6]?.priceChangePercentage < 0 ? "Down" : "Up"
                }
                title="BTC"
                subtitle={marketInfo?.latestMarketPrices[6]?.price.toString()}
                change={marketInfo?.latestMarketPrices[6]?.priceChangePercentage}
              />
            </Grid>
          </Grid>
        ) : (
          <></>
        )}
        {updatedNews && updatedNews.length > 3 ? (
          <>
            <Box fontSize="1.1rem" marginTop="2rem" fontWeight="700" marginBottom="-1rem">
              Latest News for your Stocks
            </Box>
            <Box className={clsx(classes.cardDeck, classes.flexColumnFlexXlRow)} mt={"2rem"}>
              <CardNews
                ticker={updatedNews ? updatedNews[0]?.etoroTicker : "ETSY"}
                date={updatedNews ? updatedNews[0]?.publishTime + " EST" : "2021-05-01 - 18:32"}
                image={
                  updatedNews
                    ? updatedNews[0].image
                    : "data:image/png;base64,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"
                }
                title={
                  updatedNews
                    ? updatedNews[0]?.title
                    : "Etsy (ETSY) Q1 Earnings to Gain From Marketplace Strength"
                }
                description={
                  updatedNews
                    ? updatedNews[0]?.description
                    : "Etsy's (ETSY) first-quarter performance is likely to have benefited from strengthening search capabilities, free shipment and connected device experience on the Marketplace platform."
                }
                source={updatedNews ? updatedNews[0]?.source : "Yahoo"}
                url={updatedNews ? updatedNews[0]?.url : ""}
              />
              <CardNews
                ticker={updatedNews ? updatedNews[1]?.etoroTicker : "AMZN"}
                date={updatedNews ? updatedNews[1]?.publishTime + " EST" : "23/04/2021 - 15:32"}
                image={
                  updatedNews
                    ? updatedNews[1].image
                    : "data:image/png;base64,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"
                }
                title={
                  updatedNews
                    ? updatedNews[1]?.title
                    : "These 24 Words from Amazon's CFO May Mean Billions for Its Key Moneymaker"
                }
                description={
                  updatedNews
                    ? updatedNews[1]?.description
                    : "One business unit in particular is seeing more and more demand, and the trend appears likely to continue."
                }
                source={updatedNews ? updatedNews[1]?.source : "Yahoo"}
                url={updatedNews ? updatedNews[1]?.url : ""}
              />
              <CardNews
                ticker={updatedNews ? updatedNews[2]?.etoroTicker : "BNGO"}
                date={updatedNews ? updatedNews[2]?.publishTime + " EST" : "2021-05-10 12:01:30"}
                image={
                  updatedNews
                    ? updatedNews[2].image
                    : "data:image/png;base64,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"
                }
                title={
                  updatedNews
                    ? updatedNews[2]?.title
                    : "Bionano (BNGO) to Report Q1 Earnings: What's in the Cards?"
                }
                description={
                  updatedNews
                    ? updatedNews[2]?.description
                    : "Bionano Genomics (BNGO) is expected to provide updates on its earnings and revenues when it reports first-quarter 2021 result."
                }
                source={updatedNews ? updatedNews[2]?.source : "Zacks Investment Research"}
                url={updatedNews ? updatedNews[2]?.url : ""}
              />
            </Box>
          </>
        ) : null}
      </Container>
    </>
  );
}

export default AlternativeDashboard;
